import { motion } from "framer-motion";
import { ShoppingBag, Stethoscope, Scissors, Home } from "lucide-react";
import { Link } from "react-router-dom";

const services = [
  {
    icon: ShoppingBag,
    title: "Pet Pantry",
    description:
      "Free pet food, treats, blankets, and kennel supplies for fur parents who need help. No judgment, just love.",
    image: "https://images.pexels.com/photos/8434633/pexels-photo-8434633.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    link: "/pet-pantry",
    linkText: "Request Help",
    span: "md:col-span-7",
  },
  {
    icon: Stethoscope,
    title: "Veterinary Resources",
    description:
      "Directory of affordable veterinary services and emergency care options. Because every pet deserves medical attention.",
    image: "https://images.unsplash.com/photo-1584738620467-51b852c2af2e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxkb2clMjB2ZXQlMjBjYXJlJTIwaGFuZHN8ZW58MHx8fHwxNzc1OTcxNTEzfDA&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-5",
  },
  {
    icon: Scissors,
    title: "Grooming Services",
    description:
      "Volunteer-powered grooming to keep your fur babies clean, healthy, and feeling loved.",
    span: "md:col-span-5",
  },
  {
    icon: Home,
    title: "Shelter Support",
    description:
      "We supply shelters with food, treats, and medical care. Together we can make sure no animal goes without.",
    span: "md:col-span-7",
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.15, ease: "easeOut" },
  }),
};

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-32 bg-[#F2EFEB]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          className="mb-16"
        >
          <p className="font-body text-xs font-bold tracking-[0.2em] uppercase text-[#C0603A] mb-4">
            What We Do
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight text-[#2C2C2A] font-medium max-w-lg">
            Services & Programs
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
          {services.map((svc, i) => (
            <motion.div
              key={svc.title}
              custom={i}
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              className={`${svc.span} bg-[#FDFBF7] border border-[#E8E3DD] rounded-3xl overflow-hidden group hover:shadow-[0_12px_48px_rgba(192,96,58,0.08)] transition-shadow duration-500`}
              data-testid={`service-card-${svc.title.toLowerCase().replace(/\s/g, "-")}`}
            >
              {svc.image && (
                <div className="h-48 overflow-hidden">
                  <img
                    src={svc.image}
                    alt={svc.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
              )}
              <div className="p-8 md:p-10">
                <svc.icon className="w-8 h-8 text-[#C0603A] mb-4" />
                <h3 className="font-heading text-2xl text-[#2C2C2A] mb-3">{svc.title}</h3>
                <p className="font-body text-sm leading-relaxed text-[#6C6B66] mb-4">
                  {svc.description}
                </p>
                {svc.link && (
                  <Link
                    to={svc.link}
                    className="font-body text-sm font-semibold text-[#C0603A] hover:text-[#A64D29] transition-colors inline-flex items-center gap-1"
                    data-testid={`service-link-${svc.title.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {svc.linkText} &rarr;
                  </Link>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
